import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { hexColors } from '@mobi/settings'
import { SilkFallback } from '@core/Areas/Account/Pages/Activity/Components/Ticket/Components'
import { Investment } from '@core/Areas/Account/Pages/Activity/Components/Ticket/Components/Investment'
import {
  getNumberFromString,
  getSingleTicketTitle,
  shouldUseFallbackIcon,
} from '@core/Areas/Account/Pages/Activity/Components/Ticket/helpers'
import { TicketDisplayContext } from '@core/Areas/Account/Pages/Activity/context'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import { FixedOddsMarketDisplayNames } from '@core/Areas/RaceCard/constants'
import type { NextSkyRace } from '@core/Areas/RaceCardSky/types'
import { meetingIdToContestType } from '@core/Areas/Racing/helpers'
import { SilkImageContained } from '@core/Components/SilkImage/SilkImageContained'
import { getMatchedSkyRaceForTicketLeg } from '../../helpers/getMatchedSkyRaceForTicketLeg'
import { SkyRaceHeader } from '../SkyRaceHeader'
import { FallbackRaceHeader } from './Components/FallbackRaceHeader'

const enum LocalConstants {
  DogsSilkClass = 'multi__dogs-silk',
}

export const MultiTicket: React.FC<{ ticket: Ticket; nextSkyRaces: NextSkyRace[] }> = ({
  ticket,
  nextSkyRaces,
}) => {
  return (
    <TicketDisplayContext.Provider
      value={{ type: 'pending-bets-drawer', data: { isSkyRaceMatch: false } }}
    >
      <WrapperStyled data-test='MultiTicket'>
        <header>
          <h3>Multi @ {getNumberFromString(ticket.combinedDividend?.toString() || '')}</h3>
        </header>

        <div>
          <ul>
            {ticket.legs.map(leg => {
              const legTitle =
                leg.betType === FixedOddsMarketDisplayNames.H2H
                  ? `${leg.betType} - ${leg.runners}`
                  : getSingleTicketTitle(leg, ticket.planSeq).title

              const shouldHideSilk = shouldUseFallbackIcon(leg, ticket.planSeq)
              const contestType = meetingIdToContestType(leg.fixtureId)
              const isDogsSilk = contestType === 'dogs'

              const cleanBetType = (leg.betType || '').split('-')[0].trim()
              const cleanLegReturn = (leg.return || '').split(',')[0].replaceAll(/[a-zA-Z|$]/gi, '')

              const matchedSkyRace = getMatchedSkyRaceForTicketLeg(leg, nextSkyRaces)

              return (
                <li key={legTitle}>
                  {matchedSkyRace ? (
                    <SkyRaceHeader nextSkyRace={matchedSkyRace} />
                  ) : (
                    <FallbackRaceHeader leg={leg} />
                  )}
                  <section>
                    <div
                      className={!shouldHideSilk && isDogsSilk ? LocalConstants.DogsSilkClass : ''}
                    >
                      {shouldHideSilk ? (
                        <SilkFallback contestType={contestType} />
                      ) : (
                        <SilkImageContained
                          fixtureDate={leg.fixtureDate}
                          fixtureId={leg.fixtureId}
                          raceNumber={leg.selections[0].raceNumber || leg.raceNumber}
                          acceptorNumber={
                            leg.selections[0].selectionDetails[0].acceptorDetails[0].number
                          }
                        />
                      )}
                    </div>

                    <div>
                      <h4>
                        {legTitle} {cleanLegReturn && `@ ${cleanLegReturn}`}
                      </h4>
                      {cleanBetType && <span>{cleanBetType}</span>}
                    </div>
                  </section>
                </li>
              )
            })}
          </ul>
        </div>

        <footer>
          <Investment ticket={ticket} status='Pending' />
        </footer>
      </WrapperStyled>
    </TicketDisplayContext.Provider>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '> header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: spacing.smx1,
    margin: 0,

    '> h3': {
      display: 'flex',
      alignItems: 'center',
      background: 0,
      color: colors.black,
      margin: 0,
      padding: 0,
      fontFamily: font.family.primary,
      fontSize: font.size.sm.fontSize,
      letterSpacing: font.size.sm.letterSpacing,
      lineHeight: font.size.sm.lineHeight,
      fontWeight: font.weight.semibold,
    },
  },

  '> div:first-of-type': {
    padding: 0,

    '> ul': { listStyle: 'none', padding: 0, margin: 0 },

    '> ul > li': {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.smx1,
      margin: 0,
      marginBottom: spacing.smx1,
      padding: spacing.smx1,
      borderRadius: radius.sm,
      border: '0.5px solid ' + colors.neutral[200],

      '&:last-of-type': { marginBottom: 0 },

      '> section': {
        display: 'flex',
        alignItems: 'center',

        '> div:first-of-type': {
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '3rem',
          height: '3rem',
          borderRadius: '50%',
          marginRight: '1rem',
          background: hexColors.gainsboro,
          color: hexColors.dimGrey,

          [`&.${LocalConstants.DogsSilkClass}`]: {
            background: 'none',
          },
        },

        '> div:last-of-type': {
          h4: {
            padding: 0,
            margin: 0,
            textAlign: 'left',
            fontFamily: font.family.primary,
            fontSize: font.size.sm.fontSize,
            letterSpacing: font.size.sm.letterSpacing,
            lineHeight: font.size.sm.lineHeight,
            fontWeight: font.weight.semibold,
          },

          span: {
            fontFamily: font.family.primary,
            fontSize: font.size.sm.fontSize,
            letterSpacing: font.size.sm.letterSpacing,
            lineHeight: font.size.sm.lineHeight,
            fontWeight: font.weight.regular,
          },
        },
      },
    },
  },

  '> footer': {
    paddingTop: spacing.smx2,
  },
})
